// Esecuzione isolata del custom script (+ script di test) della chatroom nella
// chat di anteprima. Replica l'API window.CrafterChat di produzione per la parte
// di passaggio context, dentro un iframe same-origin con storage shimmato.

// Neutralizza la sequenza di chiusura </script> così il codice utente non può
// uscire dallo <script> inline del documento sandbox.
function escapeScript(code) {
  return String(code || '').replace(/<\/script>/gi, '<\\/script>');
}

export function buildSandboxHtml({ apiBaseUrl, conversationId, conversationToken, customScript = '', testScript = '' }) {
  const cfg = JSON.stringify({
    apiBaseUrl: String(apiBaseUrl || ''),
    conversationId: String(conversationId || ''),
    conversationToken: String(conversationToken || ''),
  }).replace(/</g, '\\u003c');
  return [
    '<!doctype html><html><head><meta charset="utf-8"></head><body><script>',
    '(function(){',
    '  var CFG = ' + cfg + ';',
    '  var _ls = {};',
    '  try { Object.defineProperty(window, "localStorage", { configurable: true, value: {',
    '    getItem: function(k){ return Object.prototype.hasOwnProperty.call(_ls, k) ? _ls[k] : null; },',
    '    setItem: function(k, v){ _ls[k] = String(v); },',
    '    removeItem: function(k){ delete _ls[k]; },',
    '    clear: function(){ _ls = {}; }',
    '  } }); } catch (e) {}',
    '  var _cookie = "";',
    '  try { Object.defineProperty(document, "cookie", { configurable: true,',
    '    get: function(){ return _cookie; },',
    '    set: function(v){ var p = String(v).split(";")[0]; _cookie = _cookie ? (_cookie + "; " + p) : p; }',
    '  }); } catch (e) {}',
    '  var listeners = {};',
    '  function on(evt, cb){ (listeners[evt] = listeners[evt] || []).push(cb); }',
    '  function off(evt, cb){ if (listeners[evt]) listeners[evt] = listeners[evt].filter(function(f){ return f !== cb; }); }',
    '  function post(type, extra){ parent.postMessage(Object.assign({ __crafterSandbox: type }, extra || {}), "*"); }',
    '  var _pending = [];',
    '  function addContext(obj){',
    '    var p = fetch(CFG.apiBaseUrl + "/chat/" + CFG.conversationId + "/context", {',
    '      method: "POST", headers: { "Content-Type": "application/json", "X-Conversation-Token": CFG.conversationToken },',
    '      body: JSON.stringify({ context: obj || {} })',
    '    }).then(function(r){ if (!r.ok) throw new Error("addContext failed: " + r.status); });',
    '    _pending.push(p); return p;',
    '  }',
    '  window.CrafterChat = {',
    '    open: function(){}, close: function(){}, toggle: function(){}, isOpen: function(){ return true; },',
    '    sendMessage: function(t){ post("sendMessage", { text: String(t == null ? "" : t) }); },',
    '    addContext: addContext,',
    '    getConversationId: function(){ return CFG.conversationId || null; },',
    '    newConversation: function(){ post("newConversation"); },',
    '    on: on, off: off',
    '  };',
    '  try {\n' + escapeScript(testScript) + '\n} catch (e) { console.error("[CrafterChat test script]", e); }',
    '  try {\n' + escapeScript(customScript) + '\n} catch (e) { console.error("[CrafterChat custom script]", e); }',
    '  Promise.allSettled(_pending).finally(function(){ post("contextSettled"); });',
    '})();',
    // Chiusura LETTERALE del tag: il </script> reale che termina lo <script> del documento.
    // (L'escaping <\/script> serve SOLO dentro il codice utente — vedi escapeScript — non qui.)
    '</script></body></html>',
  ].join('\n');
}

export function makeSandboxMessageHandler({ onSendMessage, onNewConversation, onContextSettled }) {
  return (data) => {
    if (!data || !data.__crafterSandbox) return;
    if (data.__crafterSandbox === 'sendMessage') onSendMessage(data.text || '');
    else if (data.__crafterSandbox === 'newConversation') onNewConversation();
    else if (data.__crafterSandbox === 'contextSettled') onContextSettled();
  };
}

export function runCustomScriptSandbox({
  apiBaseUrl,
  conversationId,
  conversationToken,
  customScript = '',
  testScript = '',
  onSendMessage = () => {},
  onNewConversation = () => {},
  contextTimeoutMs = 4000,
}) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;border:0;';

  let resolveReady;
  const ready = new Promise((res) => { resolveReady = res; });
  let settled = false;
  const finish = () => { if (!settled) { settled = true; resolveReady(); } };

  const route = makeSandboxMessageHandler({
    onSendMessage,
    onNewConversation,
    onContextSettled: finish,
  });
  const onMessage = (e) => {
    if (e.source !== iframe.contentWindow) return;
    route(e.data);
  };
  window.addEventListener('message', onMessage);
  const timer = setTimeout(finish, contextTimeoutMs);

  iframe.srcdoc = buildSandboxHtml({ apiBaseUrl, conversationId, conversationToken, customScript, testScript });
  document.body.appendChild(iframe);

  const destroy = () => {
    finish();
    clearTimeout(timer);
    window.removeEventListener('message', onMessage);
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  return { iframe, ready, destroy };
}
